import type zh from '../zh/eval';

/**
 * Frozen copy of the eval text the UI showed before the zh/en switch, rendered while
 * UI_I18N_ENABLED is off (gamma / prod). Do not edit the text; add a key here only when
 * zh gains one, with the text the UI would show without the switch. Delete with the flag.
 */
const evalNs: typeof zh = {
  agentTitle: 'Eval - {{agent}}',
  backToRocky: 'Back to Rocky',
  denied: {
    title: 'Evaluation Access Denied',
    account: 'Account <strong>{{uid}}</strong> is not authorized to access the Evaluation module.',
    group: 'Required LDAP group: <code>{{group}}</code>',
    hint: 'Please contact your administrator to request access.',
    back: '← Back to Rocky Portal',
  },
  crumbs: {
    eval: 'Eval',
    homepage: 'Homepage',
    usage: 'Usage',
    insights: 'Insights',
    tasks: 'Tasks',
    reports: 'Reports',
    datasets: 'Datasets',
    candidates: 'Candidates',
    evaluators: 'Evaluators',
    metrics: 'Metrics',
    settings: 'Settings',
  },
  header: {
    openNav: 'Open navigation',
    feedback: 'Feedback',
    notifications: 'Notifications',
    help: 'Help',
    settings: 'Settings',
  },
  sidebar: {
    expand: 'Expand sidebar',
    collapse: 'Collapse sidebar',
    details: 'Details',
    platform: 'Evaluate Platform',
    homepage: 'Homepage',
    reports: 'Reports',
    usage: 'Usage',
    insights: 'Insights',
    tasks: 'Tasks',
    datasets: 'Golden Datasets',
    evaluators: 'Evaluators',
  },
  placeholder: {
    body: 'Page under development. Content will be added based on the design.',
  },
  landing: {
    title: 'Eval Platform',
    description:
      'A unified platform for evaluating, monitoring, and optimizing AI agent performance across the full lifecycle. Measure quality through online and offline evaluations with custom LLM-as-judge evaluators, track usage analytics and response latency, manage golden datasets for regression testing, and surface actionable insights to continuously improve agent reliability and user experience.',
    descLabel: 'Description information',
    activeUsers: 'ACTIVE USERS',
    sessions: 'SESSIONS',
    noPermission: 'NO PERMISSION',
    agents: {
      rocky: 'Internal data analytics agent for CMHK business teams.',
      acquisition_agent: 'Customer acquisition workflow automation agent.',
      campaign_copilot_agent: 'Campaign planning and execution copilot.',
      marketing_agent: 'Marketing agent for campaign planning, content and analysis.',
    },
  },
  home: {
    filters: { today: 'Today', d7: '7D', d30: '30D', m3: '3M' },
    viewAll: 'View all',
    noEvalData: 'No eval data for this agent yet.',
    usage: 'Usage',
    usageLink: 'Usage analytics',
    loadingUsage: 'Loading usage...',
    usageFailed: 'Failed to load usage',
    noUsage: 'No usage data for this period.',
    activeUsers: 'Active Users',
    sessions: 'Sessions',
    messages: 'Messages',
    toolCalls: '{{n}} tool calls',
    tokens: 'Tokens',
    tokensSplit: '{{input}} in · {{output}} out',
    latency: 'Avg / P95 Response',
    channels: 'Channel distribution',
    noChannels: 'No channel data.',
    online: 'Online',
    loadingStats: 'Loading stats...',
    statsFailed: 'Failed to load stats',
    noStats: 'No stats available for this period.',
    offline: 'Offline',
    loading: 'Loading...',
    metricsFailed: 'Failed to load metrics',
    noRuns: 'No pre-deployment runs in this period.',
    avgPassRate: 'Avg Pass Rate',
    acrossOne: 'across {{n}} run',
    acrossMany: 'across {{n}} runs',
    promotionRate: 'Promotion Rate',
    promoted: '{{n}} promoted',
    skipped: '{{n}} skipped',
    blocked: '{{n}} blocked',
    integration: 'Integration: {{passed}}/{{total}} passed',
    integrationRunning: 'Integration: {{passed}}/{{total}} passed · {{running}} running',
    errorLog: 'Error Log',
    insightsLink: 'Dive into insights',
    loadingFailures: 'Loading failures...',
    failuresFailed: 'Failed to load failures',
    noFailures: 'No failing cases flagged. 🎉',
    search: 'Find resources',
    searchLabel: 'Filter error log',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    colName: 'Name',
    colReason: 'Failure Reason',
    colCategory: 'Category',
    noMatch: 'No matching cases.',
  },
  shared: {
    /** Locale for last-active / created times in the Eval tables. */
    dateLocale: 'zh-CN',
    /** Locale for the "Last update time" stamp; '' is the browser's default. */
    updatedLocale: '',
    lastUpdate: 'Last update time: {{time}}',
    startDate: 'Start date',
    endDate: 'End date',
    noData: 'No data available',
    findResources: 'Find resources',
    prev: '← Prev',
    next: 'Next →',
    page: 'Page {{page}} / {{total}}',
  },
  usage: {
    title: 'Eval - {{agent}} Usage',
    loading: 'Loading usage metrics…',
    loadFailed: 'Failed to load metrics',
    section: 'Usage',
    activeUsers: 'Active Users',
    sessions: 'Sessions',
    messages: 'Messages',
    messagesSub: '{{conversations}} conversations · {{toolCalls}} tool calls',
    inputTokens: 'Input Tokens',
    outputTokens: 'Output Tokens',
    toolCalls: 'Tool Calls',
    avgResponse: 'Avg Response',
    p95Response: 'P95 Response',
    latencyBreakdown: 'Latency breakdown',
    scheduled: 'Scheduled:',
    interactive: 'Interactive:',
    dailySessions: 'Daily Sessions',
    dayBar: '{{date}}: {{n}} sessions',
    channels: 'Channel Distribution',
    responseTimes: 'Response Time Distribution ({{n}} samples)',
    topTools: 'Top Tools',
    colTool: 'Tool',
    colCalls: 'Calls',
    allUsers: 'All Users ({{n}})',
    searchUsers: 'Search users',
    colUser: 'User',
    colRole: 'Role',
    colSessions: 'Sessions',
    colTotalTokens: 'Total Tokens',
    colInputTokens: 'Input Tokens',
    colOutputTokens: 'Output Tokens',
    colChannel: 'Channel',
    colLastActive: 'Last Active',
  },
  insights: {
    title: 'Eval - {{agent}} Insights',
    reportDate: 'Report date',
    latestReport: 'Latest Report',
    reportOption: '{{date}} ({{n}} sessions)',
    loading: 'Loading insights report…',
    loadFailed: 'Failed to load insights',
    noData: 'No insights data available',
    tabOverview: 'Intent Overview',
    tabOptimization: 'Optimization',
    tabFailures: 'Failure Cases',
    intentSessions: 'Sessions: {{n}}',
    description: 'Description',
    conversation: 'Session conversation',
    loadingConversation: 'Loading conversation...',
    conversationFailed: 'Failed to load conversation',
    noConversation: 'No conversation messages found.',
    loadingCases: 'Loading failure cases…',
    casesFailed: 'Failed to load failure cases',
    sourceRun: 'Source run:',
    noCases: 'No failure cases found for this period.',
    score: 'Score: {{score}}',
    session: 'Session:',
    trace: 'Trace: {{id}}',
    span: 'Span: {{id}}',
    rootCause: 'ROOT CAUSE',
    suggestion: 'SUGGESTION',
    explanation: 'ORIGINAL EVALUATOR EXPLANATION',
    noOptimization: 'No optimization data available',
    optimizationCategory: 'Optimization category',
    sessionsOf: '{{n}}/{{total}} sessions',
    affectedSessions: 'Affected sessions',
    rootCauses: 'Root causes',
    rootCauseName: 'Root cause: {{name}}',
    recommendation: 'Recommendation: {{text}}',
  },
  tasks: {
    title: 'Eval - {{agent}} Task',
    loading: 'Loading task metrics…',
    loadFailed: 'Failed to load task metrics',
    section: 'Usage',
    activeTasks: 'Active Tasks',
    executions: 'Executions',
    successRate: 'Success Rate',
    failed: 'Failed',
    dailyExecutions: 'Daily Executions',
    success: 'Success',
    dayBar: '{{date}}: {{success}} success, {{failed}} failed',
    typeDistribution: 'Task Type Distribution',
    colType: 'Type',
    colCount: 'Count',
    durationDistribution: 'Duration Distribution',
    colBucket: 'Bucket',
    topFailed: 'Top Failed Tasks ({{n}})',
    allTasks: 'All Tasks ({{n}})',
    noFailing: 'No failing tasks.',
    colName: 'Name',
    colOwner: 'Owner',
    colFailures: 'Failures',
    colLastError: 'Last Error',
    colLastFailure: 'Last Failure',
    colCreator: 'Creator',
    colSchedule: 'Schedule',
    colLastStatus: 'Last Status',
    colLastExecution: 'Last Execution',
    colCreated: 'Created',
    colExecs: 'Execs ({{n}}d)',
    colFails: 'Fails ({{n}}d)',
  },
  datasets: {
    agentDefault: 'rocky (default)',
    title: 'Eval - Rocky - Golden Datasets',
    subtitle: 'Manage offline evaluation test cases — create, edit, and organize golden datasets',
    header: 'Golden Datasets',
    info: 'Info',
    delete: 'Delete',
    edit: 'Edit',
    reviewCandidates: 'Review Candidates ({{n}})',
    importJson: 'Import JSON',
    addCase: 'Add New Test Case',
    allSuites: 'All Suites',
    allStatuses: 'All Statuses',
    statusPlaceholder: 'Status',
    loading: 'Loading test cases...',
    noCases: 'No test cases',
    noMatches: 'No matches',
    noCasesBody: 'No golden test cases found. Create one to get started.',
    viewAll: 'View all',
    status: { active: 'Active', inactive: 'Inactive', draft: 'Draft' },
    col: {
      id: 'Test Case ID',
      mode: 'Mode',
      prompt: 'Input Prompt',
      suite: 'Suite',
      profile: 'Evaluation Profile',
      source: 'Source',
      creator: 'Creator',
      status: 'Status',
      agent: 'Runtime agent',
    },
    multiTurn: 'Multi-turn',
    singleTurn: 'Single-turn',
    legacyAlias: '{{alias}} (legacy)',
    source: { candidate: 'Eval Candidate', migration: 'Migration', import: 'Import', manual: 'Manual' },
    confirmDelete: 'Delete {{n}} test case(s)?',
    deleted: 'Deleted {{n}} test case(s)',
    deleteFailed: 'Delete failed: {{error}}',
    unknownError: 'Unknown error',
    imported: 'Imported {{n}} case(s) successfully',
    importFailed: 'Import failed: {{error}}',
    profileRequired: 'Evaluation profile is required',
    updated: 'Test case updated',
    created: 'Test case created',
    opFailed: 'Operation failed: {{error}}',
    oneTurn: 'Only 1 turn detected — saving as single-turn',
    modal: {
      editTitle: 'Edit Test Case',
      createTitle: 'Create New Test Case',
      cancel: 'Cancel',
      confirm: 'Confirm',
      newSuite: '+ New suite...',
      selectSuite: 'Select suite',
      metrics: 'Metrics',
      metricsDesc: 'Select one or more',
      selectMetrics: 'Select metrics',
      channelType: 'Channel Type',
      profileDesc: 'The selected opaque profile is stored with this test case.',
      profileDisabled: '{{alias}} (disabled)',
      selectProfile: 'Select an evaluation profile',
      noProfiles: 'No evaluation profiles available',
      expectedTools: 'Expected Tools',
      commaSeparated: 'Comma-separated',
      domainTags: 'Domain Tags',
      domainTagsDesc:
        'Comma-separated. Use domain/sub_domain to tag a slice (e.g. cmhk-sales/revenue); a bare domain tags the whole domain.',
      skillAgent: 'Skill Agent',
      skillAgentDesc: "Owns this case's skill tags",
      skillAgentNoTags: 'Applies once the case has skill tags; it shows the agent the case runs on until then',
      runAgent: 'Runtime agent',
      runAgentDefault: 'Default (rocky)',
      runAgentDesc: 'The agent that actually runs this case. If unset, cases with skill tags run on their skill tag agent; others run on rocky.',
      skillTags: 'Skill Tags',
      skillTagsDesc: 'Skill names, comma-separated (all under the agent above)',
      onlineIgnore: 'Online Ignore',
      scheduled: 'Scheduled Task',
      integration: 'Integration Test',
      inputMode: 'Input Mode',
      inputPrompt: 'Input Prompt *',
      inputPromptPlaceholder: 'The user question to evaluate...',
      turns: 'Turns * (evaluated on final response)',
      turn: 'Turn {{n}}',
      turnPlaceholder: 'User message #{{n}}',
      expectedOutput: 'Expected Output - <em>Optional</em>',
      placeholder: 'This is a placeholder',
      context: 'Context',
      rawJson: 'Raw JSON',
      structured: 'Structured View',
    },
    ctx: {
      mustBeObject: 'Context must be a JSON object',
      invalidJson: 'Invalid JSON — switch to Raw JSON to fix',
      noFields: 'No context fields yet. Click "+ Add Key" to start.',
      enterValue: 'Enter {{field}}...',
      add: 'Add',
      cancel: 'Cancel',
      addKey: '+ Add Key',
    },
    importResult: {
      title: 'Import Result',
      close: 'Close',
      imported: 'Imported',
      errors: 'Errors',
      details: 'Error Details:',
    },
  },
  candidates: {
    back: '← Back to Golden Datasets',
    title: 'Golden Dataset Candidates',
    subtitle: 'Review and approve evaluation findings as golden dataset test cases.',
    awaiting: '{{n}} awaiting review',
    allReviewed: 'All reviewed',
    refresh: 'Refresh',
    rejectSelected: 'Reject Selected',
    approveSelected: 'Approve Selected',
    header: 'Candidates',
    status: { all: 'All', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' },
    search: 'Search by prompt, evaluator, or case key...',
    col: {
      prompt: 'Input Prompt',
      evaluator: 'Evaluator',
      confidence: 'Confidence',
      suite: 'Suite',
      date: 'Date',
      status: 'Status',
    },
    emptyPrompt: '(empty)',
    loading: 'Loading candidates...',
    noCandidates: 'No candidates',
    noCandidatesBody: 'No golden dataset candidates found.',
    reviewTitle: 'Review Candidate',
    reject: 'Reject',
    approve: 'Approve → Add to Golden Dataset',
    close: 'Close',
    evaluatorLabel: 'Evaluator:',
    confidenceLabel: 'Confidence:',
    sessionLabel: 'Session:',
    dateLabel: 'Date:',
    rootCause: 'Root Cause',
    noRootCause: '(no root cause)',
    evidence: 'View Full Evidence (S3)',
    fieldsTitle: 'Golden Dataset Fields (editable before approve)',
    inputPrompt: 'Input Prompt *',
    expectedBehavior: 'Expected Behavior',
    metrics: 'Metrics (comma-separated)',
    approved: 'Approved → {{id}} (draft)',
    approveFailed: 'Approve failed: {{error}}',
    unknown: 'Unknown',
    rejected: 'Candidate rejected',
    rejectFailed: 'Reject failed: {{error}}',
    bulkApproved: 'Approved {{n}} candidate(s) as draft',
    bulkApproveFailed: 'Bulk approve failed: {{error}}',
    bulkRejected: 'Rejected {{n}} candidate(s)',
    bulkRejectFailed: 'Bulk reject failed: {{error}}',
  },
  evaluators: {
    title: 'Evaluators',
    subtitle: 'Available evaluators for online and offline evaluation. Select a category to browse.',
    search: 'Search evaluators...',
    all: 'All',
    builtin: 'Built-in',
    custom: 'Custom',
    noCategories: 'No categories match',
    selectCategory: 'Select a category from the left',
    /** Keyed by the category name the page uses as its id. */
    categories: {
      'Response Quality': 'Response Quality',
      'Task Completion': 'Task Completion',
      'Tool Usage': 'Tool Usage',
      Safety: 'Safety',
      Trajectory: 'Trajectory',
      Overall: 'Overall',
      'Non-LLM': 'Non-LLM',
      Core: 'Core',
      RAG: 'RAG',
      'Content Quality': 'Content Quality',
      Agentic: 'Agentic',
      MCP: 'MCP',
      Conversational: 'Conversational',
      Multimodal: 'Multimodal',
      Other: 'Other',
      'Data Quality': 'Data Quality',
    },
    /** Keyed by evaluator id, with `.` replaced by `_`. */
    desc: {
      Builtin_Correctness: 'Evaluates whether the information in the agent\'s response is factually accurate.',
      Builtin_Faithfulness: 'Evaluates whether information in the response is supported by provided context/sources.',
      Builtin_Helpfulness: 'Evaluates from user\'s perspective how useful and valuable the agent\'s response is.',
      Builtin_ResponseRelevance: 'Evaluates whether the response appropriately addresses the user\'s query.',
      Builtin_Conciseness: 'Evaluates whether the response is appropriately brief without missing key information.',
      Builtin_Coherence: 'Evaluates whether the response is logically structured and coherent.',
      Builtin_InstructionFollowing: 'Measures how well the agent follows the provided system instructions.',
      Builtin_Refusal: 'Detects when agent evades questions or directly refuses to answer.',
      Builtin_GoalSuccessRate: 'Evaluates whether the conversation successfully meets the user\'s goals.',
      Builtin_ToolSelectionAccuracy: 'Evaluates whether the agent selected the appropriate tool for the task.',
      Builtin_ToolParameterAccuracy: 'Evaluates how accurately the agent extracts parameters from user queries.',
      Builtin_Harmfulness: 'Evaluates whether the response contains harmful content.',
      Builtin_Stereotyping: 'Detects content that makes generalizations about individuals or groups.',
      Builtin_TrajectoryExactOrderMatch: 'Exact sequence match — validates that actual tools match expected tools in exact order with no extras.',
      Builtin_TrajectoryInOrderMatch: 'In-order match — validates that expected tools appear in order within actual trajectory, extras allowed.',
      Builtin_TrajectoryAnyOrderMatch: 'Any-order match — validates that all expected tools are present regardless of order.',
      'Deval_OverallQuality-EfPz52HBTh': 'Evaluates overall session coherence, response quality, and detects critical issues (numerical scoring v1).',
      'Deval_Correctness-ruCQJ874wh': 'DeepEval-inspired correctness evaluator — assesses factual accuracy and faithfulness of agent responses.',
      'Deval_ResponseRelevance-cwiB6h5teD': 'DeepEval-inspired response relevance evaluator — assesses whether the response addresses the user\'s query.',
      'Deval_GoalSuccessRate-kolxG76EgT': 'DeepEval-inspired goal success rate evaluator — assesses whether the user\'s goal was achieved.',
      'Deval_ToolSelectionAccuracy-zQIFg18O4v': 'DeepEval-inspired tool selection accuracy evaluator — assesses whether the right tools were chosen.',
      'Deval_ToolParameterAccuracy-ZwmcLGDqLH': 'DeepEval-inspired tool parameter accuracy evaluator — assesses whether tool parameters were correctly specified.',
      ExactMatchMetric: 'Checks if the output exactly matches the expected output string.',
      PatternMatchMetric: 'Validates output against a regex or glob pattern.',
      GEval: 'General-purpose LLM-as-Judge metric with customizable evaluation criteria.',
      ConversationalGEval: 'GEval adapted for multi-turn conversational evaluation.',
      DAGMetric: 'Directed Acyclic Graph metric for evaluating structured task flows.',
      AnswerRelevancyMetric: 'Evaluates whether the response is relevant to the user\'s question.',
      FaithfulnessMetric: 'Evaluates whether the response is grounded in the retrieved context.',
      ContextualRecallMetric: 'Measures how much of the relevant information was retrieved.',
      ContextualRelevancyMetric: 'Evaluates whether the retrieved context is relevant to the query.',
      ContextualPrecisionMetric: 'Measures precision of relevant context in retrieved results.',
      HallucinationMetric: 'Detects fabricated or unsupported claims in the response.',
      BiasMetric: 'Detects biased or prejudiced content in the response.',
      ToxicityMetric: 'Evaluates whether the response contains toxic or offensive content.',
      SummarizationMetric: 'Evaluates the quality of a generated summary.',
      PIILeakageMetric: 'Detects personally identifiable information in the response.',
      NonAdviceMetric: 'Detects inappropriate advice (financial, medical, legal) in responses.',
      MisuseMetric: 'Detects potential misuse of the AI system.',
      RoleViolationMetric: 'Detects when the agent violates its assigned role boundaries.',
      RoleAdherenceMetric: 'Evaluates how well the agent stays within its defined role.',
      TaskCompletionMetric: 'Evaluates whether the agent completed the assigned task.',
      GoalAccuracyMetric: 'Measures how accurately the agent achieved the user\'s goal.',
      ToolCorrectnessMetric: 'Evaluates tool selection and parameter accuracy.',
      ToolUseMetric: 'Evaluates overall tool usage quality and necessity.',
      StepEfficiencyMetric: 'Evaluates whether the agent used the minimum steps necessary.',
      PlanAdherenceMetric: 'Evaluates how well the agent followed its planned approach.',
      PlanQualityMetric: 'Evaluates the quality of the agent\'s action plan.',
      TopicAdherenceMetric: 'Evaluates whether the agent stays on the designated topic.',
      MCPTaskCompletionMetric: 'Evaluates task completion for MCP-connected agents.',
      MCPUseMetric: 'Evaluates MCP tool usage quality.',
      ConversationCompletenessMetric: 'Evaluates whether all topics in the conversation were adequately addressed.',
      TurnRelevancyMetric: 'Evaluates per-turn relevancy in multi-turn conversations.',
      TurnFaithfulnessMetric: 'Evaluates per-turn faithfulness to context in conversations.',
      TextToImageMetric: 'Evaluates quality of text-to-image generation.',
      ImageCoherenceMetric: 'Evaluates coherence and consistency of generated images.',
      ImageHelpfulnessMetric: 'Evaluates how helpful the generated image is for the user\'s request.',
      PromptAlignmentMetric: 'Evaluates how well the output aligns with the prompt instructions.',
      JsonCorrectnessMetric: 'Validates JSON output structure and correctness.',
      KnowledgeRetentionMetric: 'Evaluates whether the agent retains context across conversation turns.',
      ArgumentCorrectnessMetric: 'Evaluates the logical correctness of arguments in the response.',
      helpfulness: 'Custom GEval metric — evaluates whether the agent delivers substantive answers vs. mere acknowledgement. Business-domain-aware.',
      correctness: 'Custom GEval metric — evaluates factual accuracy by comparing actual_output against expected_output reference.',
      data_accuracy: 'Custom GEval metric — evaluates numerical accuracy against ground-truth. 1% tolerance, handles format equivalence.',
      sql_correctness: 'Custom GEval metric — evaluates agent-generated SQL against reference. Focuses on table + filters correctness with RLS awareness.',
      completeness: 'Custom GEval metric — evaluates whether the response covers all key points.',
      conciseness: 'Custom GEval metric — evaluates whether the response is concise without unnecessary verbosity.',
      coherence: 'Custom GEval metric — evaluates logical coherence and structure.',
    },
  },
  reports: {
    // Was a fixed 'Eval - Rocky Reports': the page now names the selected agent.
    title: 'Eval - {{agent}} Reports',
    platformTitle: 'Eval - Reports',
    online: 'Online Evaluation',
    offline: 'Offline Evaluation',
  },
  continuous: {
    threshold: 'Threshold {{value}}',
    metricThreshold: '(≥ {{value}})',
    radarThresholdTip: '{{name}}: threshold ≥ {{value}}',
    radarScoreTip: '{{name}}: {{score}} (threshold ≥ {{value}})',
    conversation: 'Conversation',
    traceSummaryOne: '{{turns}} turn · {{tools}} tool calls · {{messages}} messages',
    traceSummaryMany: '{{turns}} turns · {{tools}} tool calls · {{messages}} messages',
    turn: 'Turn {{n}}',
    user: 'User',
    toolIn: 'in',
    toolOut: 'out',
    noToolIo: 'No input/output recorded',
    agentResponse: 'Agent Response',
    histogramTitle: 'Sessions & Evaluations',
    sessionsLabel: 'Sessions:',
    evaluationsLabel: 'Evaluations:',
    loadingChart: 'Loading chart...',
    noChartData: 'No data for this period',
    sessionsBar: '{{date}}: {{n}} sessions',
    evaluationsBar: '{{date}}: {{n}} evaluations',
    pageSizeOption: '{{n}} runs',
    backToDetail: '← Back to Detail',
    reportFrame: 'Evaluation Report',
    backToList: '← Back to List',
    loading: 'Loading...',
    runDetail: 'Run Detail',
    loadingReportButton: 'Loading Report...',
    viewFullReport: 'View Full Report',
    runId: 'Run ID',
    status: 'Status',
    score: 'Score',
    passThreshold: 'Pass Threshold',
    passRate: 'Pass Rate',
    sessions: 'Sessions',
    evaluations: 'Evaluations',
    hasReport: 'Has Report',
    yes: 'Yes',
    no: 'No',
    timeWindow: 'Time Window',
    start: 'Start',
    end: 'End',
    timestamps: 'Timestamps',
    createdLabel: 'Created:',
    finishedLabel: 'Finished:',
    metrics: 'Metrics',
    scoreOverview: 'Evaluator Score Overview',
    aggregatedScores: 'Aggregated Evaluator Scores',
    perEvaluator: 'Per-Evaluator Summary',
    colEvaluator: 'Evaluator',
    colAvg: 'Avg',
    colCount: 'Count',
    colPassRate: 'Pass Rate',
    reportTitle: 'Evaluation Report',
    loadingReport: 'Loading report...',
    showFullReport: 'Show Full Report',
    collapseReport: 'Collapse Report',
    sessionsTitle: 'Sessions ({{count}})',
    searchSession: 'Search Session ID...',
    allStatus: 'All Status',
    allMetrics: 'All Metrics',
    minScore: 'Min Score',
    maxScore: 'Max Score',
    clear: 'Clear',
    colSessionId: 'Session ID',
    colAvgScore: 'Avg Score',
    colStatus: 'Status',
    colLowestScore: 'Lowest Score',
    colLowestMetric: 'Lowest Metric',
    colEvaluatedAt: 'Evaluated At',
    loadingSessions: 'Loading sessions...',
    loadingSessionDetail: 'Loading session detail...',
    sessionScore: 'Session Score: {{score}}',
    evaluatorScores: 'Evaluator Scores',
    noConversation: 'No conversation data available for this session.',
    sessionsPage: 'Page {{page}} of {{pages}} ({{n}} sessions)',
    sessionsPageFiltered: 'Page {{page}} of {{pages}} ({{n}} sessions / {{total}} total)',
    prev: 'Prev',
    next: 'Next',
    evaluationsTitle: 'Evaluations ({{count}})',
    loadingEvaluations: 'Loading evaluations...',
    evaluationsFailed: 'Failed to load evaluations.',
    aiReview: {
      title: 'AI review of failed evaluations ({{count}})',
      description: 'A sample of the evaluations scored below threshold, each reviewed by an LLM against the conversation. Only critical ones become Failure Cases (Home Error Log, Insights).',
      loading: 'Loading the AI review...',
      failed: 'Failed to load the AI review.',
      empty: 'No failed evaluation was reviewed in this run.',
      verdicts: 'Verdicts:',
      severities: 'Severity:',
      falsePositiveShare: 'False positives: {{n}} of {{total}} ({{pct}}%)',
      colVerdict: 'AI verdict',
      colSeverity: 'Severity',
      colAction: 'Action',
      colEvaluator: 'Evaluator',
      colSession: 'Session',
      colScore: 'Score / threshold',
      colRootCause: 'Root cause',
      colSuggestion: 'Suggestion',
    },
    allEvaluators: 'All Evaluators',
    allSessions: 'All Sessions',
    allLevels: 'All Levels',
    levelTrace: 'Trace',
    levelSpan: 'Span',
    levelSession: 'Session',
    colSession: 'Session',
    colLevel: 'Level',
    colScore: 'Score',
    colLabel: 'Label',
    colAnalysis: 'Analysis',
    explanation: 'Explanation',
    noExplanation: 'No explanation available.',
    traceLabel: 'Trace:',
    spanLabel: 'Span:',
    dateLabel: 'Date: {{date}}',
    aiAnalysis: 'AI Analysis',
    rootCause: 'Root Cause:',
    suggestion: 'Suggestion:',
    evaluationsPage: 'Page {{page}} of {{pages}} ({{n}} evaluations)',
    showing: 'Showing {{n}} of {{total}} evaluations',
    colRunId: 'Run ID',
    colSessions: 'Sessions',
    colRunPassRate: 'Pass Rate',
    colWindow: 'Window',
    colCreated: 'Created',
    loadingRuns: 'Loading runs...',
    runsDescription: 'Evaluation runs across agent sessions.',
    refresh: 'Refresh',
    runsHeader: 'Evaluation Runs',
    searchRuns: 'Search by Run ID or status',
    filterRuns: 'Filter runs',
    noRuns: 'No evaluation runs',
    noRunsBody: 'No evaluation runs found for the selected date range.',
    preferences: 'Preferences',
    confirm: 'Confirm',
    cancel: 'Cancel',
    runsPerPage: 'Runs per page',
  },
  offline: {
    status: { SUCCESS: 'Completed', FAILED: 'Error', RUNNING: 'Running', INTERRUPTED: 'Interrupted' },
    gate: {
      pending: 'Evaluating',
      skipped: 'Skipped',
      promoted: 'Promoted',
      notPromoted: 'Not promoted',
      blocked: 'Blocked',
    },
    type: { integration: 'Integration', promotion: 'Promotion' },
    verdict: {
      integrationMeasured: 'Integration test — pass rate measured',
      integrationRunning: 'Integration test running',
      integrationRate: 'Pass rate {{rate}}% — the pipeline approval workflow decides pass/fail',
      inProgress: 'Evaluation in progress',
      interrupted: 'Interrupted by a deploy',
      interruptedSub: 'The change was returned to the queue and will re-run in the next window',
      rate: 'Pass rate {{rate}}% against threshold ≥ {{threshold}}%',
      rateUnavailable: 'Pass rate not yet available',
      notPromoted: 'Passed — not promoted',
      notPublishedReason: 'Tests passed, but the change was not published: {{reason}}',
      notPublished: 'Tests passed, but the change was not published',
      skipped: 'No test cases — skipped & promoted',
      promoted: 'Gate cleared — change promoted',
      failed: 'Run failed before completing',
      blocked: 'Gate blocked — change not promoted',
      skippedSub: 'No test cases matched this change — promoted without evaluation',
    },
    trigger: { approval: 'Approval Workflow', codeChange: 'Code change', scoped: 'Scoped run' },
    allCapabilities: 'All capabilities',
    changedCapabilities: 'Changed capabilities',
    target: 'Target',
    promotion: {
      title: 'Promotion Results',
      colTarget: 'Target',
      colStatus: 'Status',
      colVersion: 'Version',
      colNote: 'Note',
      status: { promoted: 'Promoted', skipped: 'Skipped', failed: 'Failed' },
    },
    runType: { all: 'All runs', normal: 'Promotion only', integration: 'Integration Test only' },
    pageSizeOption: '{{n}} runs',
    backToList: 'Back to list',
    loadingRun: 'Loading run…',
    overview: {
      title: 'Run Overview',
      status: 'Status',
      gate: 'Gate',
      passRate: 'Pass Rate',
      threshold: 'Threshold',
      thresholdValue: '≥ {{n}}%',
      category: 'Category',
      trigger: 'Trigger',
      created: 'Created',
      delivery: 'Delivery',
      artifacts: 'Artifacts',
      openReport: 'Open report in new tab',
    },
    testCases: 'Test Cases',
    fullReport: 'Full HTML Report',
    loadingReport: 'Loading report…',
    reportBody: 'The complete evaluation report (all cases, rendered) opens in a new tab.',
    openFullReport: 'Open Full HTML Report',
    noStoredReport:
      'No stored report — this run predates report storage in S3. Newer runs include the full HTML report here.',
    reportUnavailable: 'Report is not available for this run.',
    col: {
      runId: 'Run ID',
      type: 'Type',
      status: 'Status',
      gate: 'Gate',
      passRate: 'Pass Rate',
      category: 'Category',
      trigger: 'Trigger',
      created: 'Created',
    },
    loadingRuns: 'Loading runs…',
    preferences: 'Preferences',
    confirm: 'Confirm',
    cancel: 'Cancel',
    runsPerPage: 'Runs per page',
    description:
      'Each code change is evaluated before release — changes are promoted to production only after all test cases pass.',
    refresh: 'Refresh',
    header: 'Pre-Deployment Runs',
    filterRunType: 'Filter by run type',
    noRuns: 'No pre-deployment runs',
    noRunsBody: 'No runs found for this filter.',
  },
  cases: {
    unnamedTool: '(unnamed tool)',
    llmCallsOne: '{{n}} LLM call',
    llmCallsMany: '{{n}} LLM calls',
    tokens: '{{n}} tokens',
    noToolCalls: 'No tool calls in this run.',
    toolCallsOne: '{{n}} tool call',
    toolCallsMany: '{{n}} tool calls',
    toolIn: 'in',
    toolOut: 'out',
    loading: 'Loading test cases…',
    noCases:
      'No per-case results for this run — it predates case storage, or matched no test cases. See the full report below.',
    search: 'Search case id…',
    filterLabel: 'Filter by outcome',
    all: 'All',
    passed: 'Passed',
    failed: 'Failed',
    sortLabel: 'Sort cases',
    sortStatus: 'Sort: Failing first',
    sortHigh: 'Sort: Score high → low',
    sortLow: 'Sort: Score low → high',
    shown: '{{n}} of {{total}} shown',
    noMatch: 'No cases match these filters.',
    overall: '{{pct}} overall',
    input: 'Input',
    answer: 'Agent answer',
    metrics: 'Metrics',
    noMetrics: 'No metrics recorded for this case.',
    trace: 'Trace',
    loadingTrace: 'Loading trace…',
    traceTooLarge: 'Trace is too large to display inline — open the full HTML report below.',
    noTrace: 'No trace captured for this case.',
  },
  retry: {
    decision: { RETRY: 'Will retry', SUPERSEDED: 'Superseded', IN_FLIGHT: 'In flight' },
    nothing: 'Nothing to retry — every target was superseded by a newer change or is already in flight.',
    queuedNow: 'Retry queued — the new run starts shortly and will appear in the run list.',
    queuedLater: 'Retry queued — it starts at the next hourly sweep and will appear in the run list.',
    alreadyQueued: 'A retry of this run is already queued.',
    failed: 'Retry failed: {{error}}',
    colTarget: 'Target',
    colCategory: 'Category',
    colDecision: 'Decision',
    colReason: 'Reason',
    retry: 'Retry',
    title: 'Retry this run?',
    cancel: 'Cancel',
    queue: 'Queue retry',
    body: 'Targets unchanged since this run failed are re-evaluated with the current test cases. Targets with a newer change are skipped — that change gets its own evaluation.',
  },
  askAgent: {
    button: 'Ask Eval Agent',
    trend: 'Analyze the recent online evaluation trend of {{agent}}: how did sessions and evaluations change, and are there any unusual days?',
    runs: 'Compare the recent online evaluation runs of {{agent}}: how are score and pass rate trending, and which runs need attention most?',
    runSummary: 'Explain online evaluation run {{runId}} of {{agent}}: score {{score}}, pass rate {{passRate}} (pass threshold {{threshold}}), {{sessions}} sessions and {{evaluations}} evaluations. How did it do overall, and where are the main problems?',
    scoreOverview: 'Which evaluators scored lowest in run {{runId}}, and what caused each of them?',
    evaluator: 'In run {{runId}}, evaluator {{evaluator}} averaged {{avg}} with a pass rate of {{passRate}}. Why this score, and what do the failed cases have in common?',
    report: 'Summarize the key findings and recommendations of the evaluation report for run {{runId}}.',
    sessions: 'Which sessions scored lowest in run {{runId}}, and why did each of them fail?',
    session: 'Analyze session {{sessionId}} in run {{runId}}: average score {{score}}, lowest evaluator {{metric}} ({{lowest}}). What went wrong in this session?',
    aiReview: 'Summarize the AI review of run {{runId}}: which failures are real issues, which are false positives, and what should be fixed first?',
    aiReviewItem: 'The AI review of run {{runId}} rated evaluator {{evaluator}} on session {{sessionId}} (trace {{traceId}}) as {{verdict}} (severity {{severity}}, action {{action}}), score {{score}} / threshold {{threshold}}, root cause: {{rootCause}}. Please check this conclusion and give a concrete fix.',
    evaluations: 'In the evaluations of run {{runId}}, which evaluators fail most often, and is there a pattern?',
    evaluation: 'In run {{runId}}, evaluator {{evaluator}} scored session {{sessionId}} (trace {{traceId}}) {{score}} ({{label}}). Is this verdict right?',
    offline: {
      runs: 'Summarize the recent offline (pre-deployment) evaluation runs: which were blocked by the gate, and how is the pass rate trending?',
      run: 'Explain offline evaluation run {{runId}} (type {{type}}, status {{status}}, pass rate {{passRate}}): how did it go, and why did anything not pass?',
      verdict: 'The verdict of offline evaluation run {{runId}} is "{{verdict}}": {{detail}}. Why this verdict, and what should be done next?',
      overview: 'Summarize offline evaluation run {{runId}}: targets {{targets}}, trigger {{trigger}}, status {{status}}, pass rate {{passRate}} (threshold {{threshold}}).',
      promotion: 'How did promotion go for each target in run {{runId}}? Why were any skipped or failed?',
      promotionItem: 'In run {{runId}}, target {{target}} has promotion status {{status}} ({{note}}). Why, and what should be done?',
      cases: 'Which test cases failed in run {{runId}}, and what do the failures have in common?',
      case: 'Analyze test case {{caseId}} in offline evaluation run {{runId}}: result {{result}}, overall score {{score}}. What went wrong, and how can it be improved?',
      metric: 'In test case {{caseId}} of run {{runId}}, metric {{metric}} scored {{score}} (threshold {{threshold}}), with the reason: {{reason}}. Is this verdict right, and how can it be improved?',
    },
    tasks: {
      overview: 'Analyze the scheduled tasks of {{agent}} from {{start}} to {{end}}: {{active}} active tasks, {{executions}} executions, {{rate}} success rate, {{failed}} failures. What needs attention?',
      daily: 'How did daily task executions and failures of {{agent}} trend from {{start}} to {{end}}? Which days were unusual?',
      types: 'How are the tasks of {{agent}} distributed by type from {{start}} to {{end}}? Which type fails most?',
      durations: 'How are task durations of {{agent}} distributed from {{start}} to {{end}}? Are any tasks unusually slow?',
      failedList: 'Which tasks of {{agent}} failed most from {{start}} to {{end}}? Why, and how can they be fixed?',
      allList: 'Summarize how all scheduled tasks of {{agent}} ran from {{start}} to {{end}}: which tasks need attention?',
      failedItem: 'Task "{{name}}" (owner {{owner}}) failed {{failures}} times from {{start}} to {{end}}; last error: {{error}}. What is the cause, and how can it be fixed?',
      item: 'Analyze task "{{name}}" (type {{type}}, owner {{owner}}): last status {{status}}, {{executions}} executions and {{failures}} failures from {{start}} to {{end}}. Is it running normally?',
    },
    usage: {
      overview: 'Analyze the usage of {{agent}} from {{start}} to {{end}}: {{users}} active users, {{sessions}} sessions, {{messages}} messages, {{toolCalls}} tool calls. What stands out?',
      daily: 'How did daily sessions of {{agent}} trend from {{start}} to {{end}}? Were any days unusual?',
      channels: 'How is the usage of {{agent}} distributed across channels from {{start}} to {{end}}?',
      latency: 'How are the response times of {{agent}} distributed from {{start}} to {{end}} (average {{avg}}, P95 {{p95}})? Where do slow responses come from?',
      tools: 'Which tools did {{agent}} use most from {{start}} to {{end}}? Is the distribution of calls reasonable?',
      tool: 'Tool {{tool}} was called {{count}} times by {{agent}} from {{start}} to {{end}}. What is it mainly used for, and are there problems with its calls?',
      users: 'How is the usage of {{agent}} distributed across users from {{start}} to {{end}}? Who are the most active users?',
      user: 'User {{user}} used {{agent}} from {{start}} to {{end}}: {{sessions}} sessions, {{tokens}} tokens, channel {{channel}}. What does this user mainly use it for?',
    },
    insights: {
      intent: 'In the insights report of {{agent}} ({{date}}), user intent "{{intent}}" ranks #{{rank}} with {{sessions}} sessions. How well are these requests handled, and what can be improved?',
      categories: 'Summarize the optimization areas in the insights report of {{agent}} ({{date}}): which problems matter most, and what should be improved first?',
      category: 'In the insights report of {{agent}} ({{date}}), optimization category "{{category}}" affects {{sessions}} / {{total}} sessions with {{rootCauses}} root causes. Please analyze it and prioritize the improvements.',
      rootCause: 'Root cause "{{rootCause}}" under optimization category "{{category}}" has the recommendation: {{recommendation}}. Please explain concretely how to carry it out.',
      failures: 'Summarize the failure cases of run {{runId}}: what are the main problems, and what should be fixed first?',
      failure: 'Failure case: in run {{runId}}, evaluator {{evaluator}} scored session {{sessionId}} (trace {{traceId}}) {{score}}, verdict {{verdict}} (severity {{severity}}, action {{action}}), root cause: {{rootCause}}. Please check it and give a fix.',
    },
  },
};

export default evalNs;
